import React, { useEffect, useState } from 'react';
import { MagnifyingGlassIcon, XCircleIcon } from '@heroicons/react/24/outline';

const FILTER_OPTIONS = {
  category: [
    { value: '', label: 'All categories' },
    { value: 'crypto', label: 'Crypto' },
    { value: 'trading', label: 'Trading' },
    { value: 'news', label: 'News' },
    { value: 'other', label: 'Other' },
  ],
  language: [
    { value: '', label: 'All languages' },
    { value: 'ru', label: 'Russian' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ar', label: 'Arabic' },
    { value: 'fa', label: 'Persian' },
    { value: 'hi', label: 'Hindi' },
    { value: 'zh', label: 'Chinese' },
  ],
  assignment_type: [
    { value: '', label: 'Any source' },
    { value: 'manual', label: 'Manual' },
    { value: 'automatic', label: 'Automatic' },
  ],
};

function FilterSelect({ id, label, value, onChange, options }) {
  return (
    <label htmlFor={id} className="flex items-center gap-1 text-xs text-gray-600">
      <span>{label}</span>
      <select
        id={id}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-md border border-gray-300 px-2 py-1 text-sm bg-white"
      >
        {options.map((option) => (
          <option key={option.value || 'all'} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const GroupsSearch = ({
  pendingSearch,
  filters,
  onSearchChange,
  onFilterChange,
  onClearFilters,
  resultCount,
  totalCount,
}) => {
  // Track which countries are present in the current dataset so we only
  // surface relevant ones. Keep it lightweight — no fetch, just dedupe from
  // totalCount + pagination meta is enough; for now we show a free text input.
  const [countryInput, setCountryInput] = useState(filters.country || '');

  useEffect(() => {
    setCountryInput(filters.country || '');
  }, [filters.country]);

  const submitCountry = () => {
    if ((filters.country || '') !== countryInput) {
      onFilterChange('country', countryInput.trim());
    }
  };

  const isFiltered = Boolean(
    pendingSearch ||
      filters.category ||
      filters.language ||
      filters.country ||
      filters.assignment_type
  );

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="search"
            value={pendingSearch}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search by group name, username, or ID..."
            aria-label="Search project groups"
            className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
        </div>
        {isFiltered && (
          <button
            type="button"
            onClick={onClearFilters}
            className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-red-600"
          >
            <XCircleIcon className="h-4 w-4" />
            Clear filters
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <FilterSelect
          id="project-groups-category"
          label="Category"
          value={filters.category}
          options={FILTER_OPTIONS.category}
          onChange={(value) => onFilterChange('category', value)}
        />
        <FilterSelect
          id="project-groups-language"
          label="Language"
          value={filters.language}
          options={FILTER_OPTIONS.language}
          onChange={(value) => onFilterChange('language', value)}
        />
        <FilterSelect
          id="project-groups-assignment"
          label="Source"
          value={filters.assignment_type}
          options={FILTER_OPTIONS.assignment_type}
          onChange={(value) => onFilterChange('assignment_type', value)}
        />
        <label htmlFor="project-groups-country" className="flex items-center gap-1 text-xs text-gray-600">
          <span>Country</span>
          <input
            id="project-groups-country"
            type="text"
            value={countryInput}
            onChange={(event) => setCountryInput(event.target.value)}
            onBlur={submitCountry}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitCountry();
              }
            }}
            placeholder="e.g. Russia, US, BR"
            className="rounded-md border border-gray-300 px-2 py-1 text-sm w-40"
          />
        </label>
      </div>

      {isFiltered && (
        <div className="text-xs text-gray-500" aria-live="polite">
          {resultCount} of {totalCount} groups match{pendingSearch ? ` "${pendingSearch}"` : ''}
        </div>
      )}
    </div>
  );
};

export default GroupsSearch;